import _ from 'underscore';

import type { CourseItemType } from '@coursera/event-pulse-types';

import type AuthoringCourseBranch from 'bundles/author-branches/models/AuthoringCourseBranch';
import ContextStatusStates from 'bundles/author-course-level/constants/ContextStatusStates';
import { isPrivateContext } from 'bundles/author-course-level/utils/contextUtils';
import type { FlagCategory } from 'bundles/authoring-course-content-feedback/types/courseContentFeedback';
import type { FeedbackComments } from 'bundles/authoring-course-content-feedback/types/courseContentFeedback';
import type { AuthoringCourseContextInformation } from 'bundles/authoring/common/types/authoringCourseContexts';
import type { Category } from 'bundles/content-feedback/constants/ItemFlagCategories';
import itemFlagCategories from 'bundles/content-feedback/constants/ItemFlagCategories';

import type { ItemType } from '../constants/ItemTypes';

/**
 * Returns the visible flag categories to the current user.
 * @param itemType Item Type (optional). If not present, return categories across all items
 * @return Array of supported categories.
 */
export const getVisibleFlagCategories = (itemType: ItemType, isSuperuser: boolean) => {
  let categories;
  const categoryConstants = itemFlagCategories();

  if (itemType) {
    categories = categoryConstants[itemType];
  } else {
    categories = _(categoryConstants)
      .chain()
      .values()
      .flatten()
      .uniq((category) => category.id)
      .value();
  }

  let visibleCategories: Array<FlagCategory> = [];

  if (isSuperuser) {
    visibleCategories = categories;
  } else {
    visibleCategories = _(categories).filter((category) => category.isPublic);
  }

  return visibleCategories;
};

export const canViewItemFeedback = (
  courseContext?: AuthoringCourseContextInformation,
  authoringBranch?: AuthoringCourseBranch
): boolean => {
  if (courseContext && isPrivateContext(courseContext.typeName)) {
    return !([ContextStatusStates.NEW, ContextStatusStates.UPCOMING, ContextStatusStates.PENDING] as string[]).includes(
      courseContext.status
    );
  } else {
    return authoringBranch?.isLaunched || authoringBranch?.isBaseBranch || false;
  }
};

export const getCategoriesForLearnerItemType = (itemTypeName: ItemType, isDubbingAvailable: boolean) => {
  const categoryConstants = itemFlagCategories(isDubbingAvailable);

  const categories: Category[] = categoryConstants[itemTypeName];

  return categories;
};

export const itemTypeToCourseItemTypeMap: Record<ItemType, CourseItemType> = {
  quiz: 'quiz',
  reading: 'reading',
  lecture: 'lecture',
  programming: 'programming_assignment',
  staffGraded: 'staff_graded',
  discussionPrompt: 'discussion_prompt',
  gradedDiscussionPrompt: 'discussion_prompt',
  teammateReview: 'teammate_review',
  peer: 'peer_review',
  notebook: 'unknown',
  widget: 'widget',
  ungradedWidget: 'widget',
  workspaceLauncher: 'workspace_launcher',
  ungradedLab: 'ungraded_lab',
  gradedLti: 'lti_tool',
  ungradedLti: 'lti_tool',
  dialogue: 'dialogue',
  rolePlay: 'role_play',
};

export const mapItemTypeToCourseItemType = (itemType: ItemType): CourseItemType => {
  return itemTypeToCourseItemTypeMap[itemType] || 'unknown';
};

// The feedback flag comments is an object with multiple category types as keys and the category types can be
// re-submitted. Because of this, additional logic is needed to isolate the category type being submitted or removed for eventing.
export const getFeedbackCommentCategoryKey = (
  initalFeedbackComments: FeedbackComments,
  submittedFeedbackComment: FeedbackComments
): string => {
  // Check for if there are no existing feedback comments
  if (!initalFeedbackComments || Object.keys(initalFeedbackComments).length === 0) {
    const submittedFeedbackCommentKeys = Object.keys(submittedFeedbackComment);
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return submittedFeedbackCommentKeys.length === 1 ? submittedFeedbackCommentKeys[0] : 'unknown';
  }

  const initalFeedbackCommentsKeys = Object.keys(initalFeedbackComments);
  const submittedFeedbackCommentKeys = Object.keys(submittedFeedbackComment);

  // Check for new keys or updated values in submittedFeedbackComment
  for (const key of submittedFeedbackCommentKeys) {
    if (!initalFeedbackCommentsKeys.includes(key)) {
      return key;
    } else if (JSON.stringify(initalFeedbackComments[key]) !== JSON.stringify(submittedFeedbackComment[key])) {
      return key;
    }
  }

  // Check for removed keys in initialFeedbackComments
  for (const key of initalFeedbackCommentsKeys) {
    if (!submittedFeedbackCommentKeys.includes(key)) {
      return key;
    }
  }

  return 'unknown';
};

// Event tracking data for legacy item feedback component events
export const getItemFeedbackEventContext = (itemId: string, courseId: string) => ({
  namespace: 'content_learner.rating_items',
  include: {
    bucket_id: 'A2',
    item_id: itemId,
    course_id: courseId,
  },
});
